import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../lib/auth';
import Button from '../Button';
import Input from '../Input';
import Textarea from '../Textarea';
import TokenCostModal from '../TokenCostModal';
import { apiClient } from '../../lib/api';

interface AITaxonomyGeneratorProps {
  onTaxonomyGenerated: () => void;
  disabled?: boolean;
  onJobStarted?: (jobId: string) => void;
}

export default function AITaxonomyGenerator({ onTaxonomyGenerated, disabled, onJobStarted }: AITaxonomyGeneratorProps) {
  const { t } = useTranslation();
  const { workspace } = useAuthStore();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [productExpertise, setProductExpertise] = useState('');
  const [maxUrls, setMaxUrls] = useState(10);
  const [regenerationMode, setRegenerationMode] = useState<'fresh' | 'merge' | 'extend'>('fresh');
  const [error, setError] = useState('');

  // Get active website from workspace
  const activeWebsite = workspace?.websites?.find((w) => w.isActive);

  const resetForm = () => {
    setProductExpertise('');
    setMaxUrls(10);
    setRegenerationMode('fresh');
    setError('');
  };

  const handleConfigSubmit = () => {
    if (!activeWebsite) {
      setError('No active website found. Please select a website first.');
      return;
    }

    // Close config modal and show token cost modal
    setShowConfigModal(false);
    setShowTokenModal(true);
  };

  const handleStartGeneration = async () => {
    setError('');

    if (!activeWebsite) {
      setError('No active website found. Please select a website first.');
      throw new Error('No active website');
    }

    // Use active website URL
    let url = activeWebsite.domain;
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    const response = await apiClient.post('/taxonomy/generate-ai', {
      websiteUrl: url,
      productExpertise: productExpertise.trim() || undefined,
      maxUrls,
      regenerationMode,
    });

    const data = response.data.data;

    // Notify parent that job started
    if (onJobStarted) {
      onJobStarted(data.jobId);
    }

    // Reset form after successful start
    resetForm();
  };

  return (
    <>
      <Button onClick={() => setShowConfigModal(true)} disabled={disabled}>
        {t('settings.taxonomy.generateAITaxonomy', 'Generate AI Taxonomy')}
      </Button>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 border-2 border-black dark:border-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-3 dark:text-white">
              {t('settings.taxonomy.aiTaxonomyGeneration', 'AI-Powered Taxonomy Generation')}
            </h3>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t(
                'settings.taxonomy.aiTaxonomyDescription',
                'GPT-4o will analyze your website and generate a complete taxonomy structure with scientific terms. This process takes 5-10 minutes and you will receive an email when complete.'
              )}
            </p>

            <div className="space-y-4 mb-4">
              {/* Active Website Display */}
              <div>
                <label className="block text-sm font-medium mb-2 dark:text-white">
                  {t('settings.taxonomy.activeWebsite', 'Active Website')}
                </label>
                {activeWebsite ? (
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-black dark:border-white rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 text-2xl">🌐</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">
                          {activeWebsite.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 font-mono truncate">
                          {activeWebsite.domain}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
                    <p className="text-sm text-red-900 dark:text-red-200">
                      {t('settings.taxonomy.noActiveWebsite', 'No active website selected. Please add and select a website first.')}
                    </p>
                  </div>
                )}
              </div>

              <Textarea
                label={t('settings.taxonomy.productExpertise', 'Product/Service Description (Optional)')}
                value={productExpertise}
                onChange={(e) => setProductExpertise(e.target.value)}
                placeholder={t(
                  'settings.taxonomy.productExpertisePlaceholder',
                  'Describe your product or service to help AI generate better categories...'
                )}
                rows={3}
              />

              <div>
                <label className="block text-sm font-medium mb-2 dark:text-white">
                  {t('settings.taxonomy.regenerationMode', 'Regeneration Mode')}
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="regenerationMode"
                      value="fresh"
                      checked={regenerationMode === 'fresh'}
                      onChange={(e) => setRegenerationMode(e.target.value as 'fresh' | 'merge' | 'extend')}
                      className="w-4 h-4 mt-1"
                    />
                    <div>
                      <span className="text-sm font-medium dark:text-white">
                        🔄 {t('settings.taxonomy.modeFresh', 'Fresh - Delete all & regenerate')}
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {t('settings.taxonomy.modeFreshDesc', 'Completely removes existing taxonomy and creates a new one from scratch.')}
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="regenerationMode"
                      value="merge"
                      checked={regenerationMode === 'merge'}
                      onChange={(e) => setRegenerationMode(e.target.value as 'fresh' | 'merge' | 'extend')}
                      className="w-4 h-4 mt-1"
                    />
                    <div>
                      <span className="text-sm font-medium dark:text-white">
                        🔀 {t('settings.taxonomy.modeMerge', 'Merge - Update existing + add new')}
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {t('settings.taxonomy.modeMergeDesc', 'Updates existing nodes with new data and adds new taxonomy categories.')}
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="regenerationMode"
                      value="extend"
                      checked={regenerationMode === 'extend'}
                      onChange={(e) => setRegenerationMode(e.target.value as 'fresh' | 'merge' | 'extend')}
                      className="w-4 h-4 mt-1"
                    />
                    <div>
                      <span className="text-sm font-medium dark:text-white">
                        ➕ {t('settings.taxonomy.modeExtend', 'Extend - Only add new nodes')}
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {t('settings.taxonomy.modeExtendDesc', 'Only adds new categories, keeps all existing nodes unchanged.')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm">
                <p className="font-medium text-blue-900 dark:text-blue-200 mb-1">
                  📧 {t('settings.taxonomy.emailNotification', 'Email Notification')}
                </p>
                <p className="text-blue-800 dark:text-blue-300 text-xs">
                  {t(
                    'settings.taxonomy.emailNotificationDescription',
                    'You will receive an email when the taxonomy generation is complete (typically 5-10 minutes).'
                  )}
                </p>
              </div>
            </div>

            {error && (
              <div className="p-3 border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-sm mb-4">
                {error}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowConfigModal(false);
                  resetForm();
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleConfigSubmit} disabled={!activeWebsite}>
                {t('common.continue', 'Continue')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Token Cost Modal */}
      <TokenCostModal
        open={showTokenModal}
        onClose={() => {
          setShowTokenModal(false);
          // Optionally reopen config modal if user cancels
        }}
        onConfirm={handleStartGeneration}
        title={t('settings.taxonomy.aiTaxonomyGeneration', 'AI-Powered Taxonomy Generation')}
        description={t(
          'settings.taxonomy.tokenCostDescription',
          'Generate a complete knowledge taxonomy with AI categorization, scientific terms, and hierarchical structure.'
        )}
        tokenCost={5000}
        confirmButtonText={t('settings.taxonomy.startGeneration', 'Start Generation')}
        estimatedDuration="5-10 minutes"
      />
    </>
  );
}
